import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { tasksAPI, clientsAPI, usersAPI, chatAPI, boardsAPI, directorsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { ArrowLeft, Users2, X, UserCheck, Clock, AlertTriangle, CheckCircle2, MessageSquare, Send, Search, Filter, Bell, CalendarClock, Settings2, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { getDueDate, isOverdue, isDueSoon, formatDueDate } from '@/lib/due-dates';
import { formatClientWithGstin } from '@/lib/gstin-utils';
import { isPeriodActiveForGstin, inactiveReason } from '@/lib/gstin-period';
import SortableHeader, { sortBy } from '@/components/SortableHeader';
import ClientPickerDialog from '@/components/ClientPickerDialog';
import { FREQ_MIN_WIDTHS, FREQ_COL_WIDTHS, dueDateClass } from '@/lib/compliance-utils';
import logger from '@/lib/logger';
import ChecklistGrid from './components/ChecklistGrid';
import ReminderDialog from './components/ReminderDialog';
import OneTimeActivityGrid from './components/OneTimeActivityGrid';
import SubtaskNotePanel from './components/SubtaskNotePanel';
import { GstSyncDialog } from './components/GstSyncDialog';

const GST_RETURN_TITLES = new Set(['GSTR-1', 'GSTR-3B', 'GSTR-9', 'GSTR-9C', 'GSTR-7', 'GSTR-6', 'GSTR-4', 'CMP-08']);

const STAGE_LABELS = {
  not_started: 'Not Started',
  documents_pending: 'Docs Pending',
  documents_received: 'Docs Received',
  further_docs_required: 'Further Docs Required',
  ready_for_review: 'Ready for Review',
  tax_payable: 'Tax Payable',
  ready_to_file: 'Ready to File',
  filed: 'Filed',
  paid: 'Paid',
  not_to_be_filed: 'Not to be Filed',
  in_process: 'In Process',
  mismatched: 'Mismatched',
  matched: 'Matched',
  tax_not_to_be_paid: 'Tax Not to be Paid',
  // Checklist-stage values — used by Drafting of Financial Statements board
  docs_pending: 'Docs Pending',
  in_progress: 'In Progress',
  further_docs_required: 'Further Docs Required',
  partial_completed: 'Partial Completed',
  done: 'Done',
  // GST One-Time Activities (Registration / Cancellation / Amendment / Notice)
  application_filed: 'Application Filed',
  query_received: 'Query Received',
  query_replied: 'Query Replied',
  approved: 'Approved',
  approved_gstin_issued: 'Approved (GSTIN Issued)',
  client_onboarded: 'Client Onboarded',
  cancelled: 'Cancelled',
  received: 'Received',
  reply_drafted: 'Reply Drafted',
  reply_filed: 'Reply Filed',
  awaiting_response: 'Awaiting Response',
  further_notice_received: 'Further Notice Received',
  resolved: 'Resolved',
};

const STATUS_SHORT = {
  not_started: '--',
  documents_pending: 'DP',
  documents_received: 'DR',
  further_docs_required: 'FD',
  ready_for_review: 'RR',
  tax_payable: 'TP',
  ready_to_file: 'RF',
  filed: 'F',
  paid: 'PD',
  not_to_be_filed: 'NTF',
  in_process: 'IP',
  mismatched: 'MM',
  matched: 'M',
  tax_not_to_be_paid: 'TNP',
  docs_pending: 'DP',
  in_progress: 'IP',
  partial_completed: 'PC',
  done: 'D',
  application_filed: 'AF',
  query_received: 'QR',
  query_replied: 'QP',
  approved: 'AP',
  approved_gstin_issued: 'GI',
  client_onboarded: 'CO',
  cancelled: 'CN',
  received: 'RC',
  reply_drafted: 'RD',
  reply_filed: 'RF',
  awaiting_response: 'AR',
  further_notice_received: 'FN',
  resolved: 'RS',
};

const STATUS_CELL_COLORS = {
  not_started: 'bg-gray-50 text-gray-500',
  documents_pending: 'bg-amber-50 text-amber-700',
  documents_received: 'bg-blue-50 text-blue-700',
  further_docs_required: 'bg-orange-50 text-orange-700',
  ready_for_review: 'bg-purple-50 text-purple-700',
  tax_payable: 'bg-rose-50 text-rose-700',
  ready_to_file: 'bg-cyan-50 text-cyan-700',
  filed: 'bg-emerald-50 text-emerald-700',
  paid: 'bg-green-100 text-green-800',
  not_to_be_filed: 'bg-slate-100 text-slate-600',
  in_process: 'bg-blue-50 text-blue-700',
  mismatched: 'bg-rose-100 text-rose-800',
  matched: 'bg-emerald-50 text-emerald-700',
  tax_not_to_be_paid: 'bg-slate-100 text-slate-600',
  docs_pending: 'bg-amber-50 text-amber-700',
  in_progress: 'bg-blue-50 text-blue-700',
  partial_completed: 'bg-indigo-50 text-indigo-700',
  done: 'bg-green-50 text-green-700',
  application_filed: 'bg-cyan-50 text-cyan-700',
  query_received: 'bg-orange-50 text-orange-700',
  query_replied: 'bg-blue-50 text-blue-700',
  approved: 'bg-emerald-50 text-emerald-700',
  approved_gstin_issued: 'bg-emerald-50 text-emerald-700',
  client_onboarded: 'bg-green-100 text-green-800',
  cancelled: 'bg-slate-100 text-slate-600',
  received: 'bg-amber-50 text-amber-700',
  reply_drafted: 'bg-blue-50 text-blue-700',
  reply_filed: 'bg-cyan-50 text-cyan-700',
  awaiting_response: 'bg-purple-50 text-purple-700',
  further_notice_received: 'bg-rose-100 text-rose-800',
  resolved: 'bg-green-50 text-green-700',
};

const DEFAULT_STAGES = ['not_started', 'documents_pending', 'documents_received', 'ready_for_review', 'ready_to_file', 'filed'];

const FREQ_BADGE = {
  monthly: { label: 'Monthly', color: 'bg-blue-50 text-blue-600 border-blue-200' },
  quarterly: { label: 'Quarterly', color: 'bg-amber-50 text-amber-600 border-amber-200' },
  annual: { label: 'Annual', color: 'bg-purple-50 text-purple-600 border-purple-200' },
  one_time: { label: 'One Time', color: 'bg-gray-50 text-gray-600 border-gray-200' },
};

const DEFAULT_FY = '2026-27';
const DEFAULT_FY_START = 2026;

const getMonthlyPeriods = (fyStart) => {
  const start = fyStart || DEFAULT_FY_START;
  const months = [];
  for (let m = 4; m <= 12; m++) months.push(`${start}-${String(m).padStart(2, '0')}`);
  for (let m = 1; m <= 3; m++) months.push(`${start + 1}-${String(m).padStart(2, '0')}`);
  return months;
};

const getQuarterlyPeriods = (fyStart) => {
  const start = fyStart || DEFAULT_FY_START;
  return [`${start}-Q1`, `${start}-Q2`, `${start}-Q3`, `${start}-Q4`];
};
const getAnnualPeriods = (fy) => [`FY${fy || DEFAULT_FY}`];

const getPeriodsForFrequency = (freq, fy) => {
  const fyStr = fy || DEFAULT_FY;
  const fyStart = parseInt(fyStr.split('-')[0]);
  if (freq === 'quarterly') return getQuarterlyPeriods(fyStart);
  if (freq === 'half_yearly') return [`${fyStart}-H1`, `${fyStart}-H2`];
  if (freq === 'annual' || freq === 'annually' || freq === '3_yearly') return getAnnualPeriods(fyStr);
  if (freq === 'one_time') return [`OT-${fyStr}`];
  return getMonthlyPeriods(fyStart);
};

const getPeriodLabel = (period) => {
  if (period.includes('H1')) return 'H1 (Apr-Sep)';
  if (period.includes('H2')) return 'H2 (Oct-Mar)';
  if (period.startsWith('FY')) return `FY ${period.replace('FY', '')}`;
  if (period.startsWith('OT-')) return 'One Time';
  if (period.includes('-Q')) {
    const q = period.split('-Q')[1];
    return `Q${q} ${period.split('-')[0]}`;
  }
  const [y, m] = period.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m) - 1]}'${y.slice(2)}`;
};

const QUARTER_LABELS = { Q1: 'Q1 (Apr-Jun)', Q2: 'Q2 (Jul-Sep)', Q3: 'Q3 (Oct-Dec)', Q4: 'Q4 (Jan-Mar)' };
const HALF_LABELS = { H1: 'H1 (Apr-Sep)', H2: 'H2 (Oct-Mar)' };

const DueDateSettings = ({ freq, rule, open, onToggle, onSave, saving }) => {
  const [localRule, setLocalRule] = React.useState(rule || {});
  React.useEffect(() => { setLocalRule(rule || {}); }, [rule]);

  const hasRule = rule && Object.keys(rule).length > 0;

  const handleSave = () => {
    if (Object.keys(localRule).length === 0) return;
    onSave(localRule);
  };

  const handleClear = () => {
    onSave(null);
    setLocalRule({});
  };

  const renderMonthlyForm = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <label className="text-xs text-muted-foreground w-24 shrink-0">Due Day</label>
        <Input type="number" min={1} max={31} className="h-8 w-20 text-sm" value={localRule.day || ''} onChange={e => setLocalRule(p => ({ ...p, day: parseInt(e.target.value) || '', next_month: p.next_month !== false }))} data-testid="due-day-input" />
        <span className="text-xs text-muted-foreground">of next month</span>
      </div>
      <div className="border-t pt-3">
        <p className="text-xs text-muted-foreground mb-2">Exception (e.g., March due on April 30)</p>
        <div className="flex items-center gap-2">
          <Select value={Object.keys(localRule.exceptions || {})[0] || '_none'} onValueChange={v => {
            if (v === '_none') { setLocalRule(p => ({ ...p, exceptions: {} })); return; }
            setLocalRule(p => ({ ...p, exceptions: { [v]: p.exceptions?.[v] || { month: parseInt(v) + 1, day: 30 } } }));
          }}>
            <SelectTrigger className="h-8 w-28 text-xs"><SelectValue placeholder="Month" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none"><span className="text-xs">No exception</span></SelectItem>
              {['1','2','3','4','5','6','7','8','9','10','11','12'].map(m => (
                <SelectItem key={m} value={m}><span className="text-xs">{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)-1]}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
          {Object.entries(localRule.exceptions || {}).map(([srcMonth, exc]) => (
            <div key={srcMonth} className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">→ Month</span>
              <Input type="number" min={1} max={12} className="h-8 w-16 text-sm" value={exc.month || ''} onChange={e => setLocalRule(p => ({ ...p, exceptions: { ...p.exceptions, [srcMonth]: { ...exc, month: parseInt(e.target.value) || '' } } }))} />
              <span className="text-xs text-muted-foreground">Day</span>
              <Input type="number" min={1} max={31} className="h-8 w-16 text-sm" value={exc.day || ''} onChange={e => setLocalRule(p => ({ ...p, exceptions: { ...p.exceptions, [srcMonth]: { ...exc, day: parseInt(e.target.value) || '' } } }))} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderQuarterlyForm = () => (
    <div className="grid grid-cols-2 gap-3">
      {['Q1','Q2','Q3','Q4'].map(q => (
        <div key={q} className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-24 shrink-0">{QUARTER_LABELS[q]}</span>
          <span className="text-xs text-muted-foreground">Month</span>
          <Input type="number" min={1} max={12} className="h-8 w-16 text-sm" value={localRule[q]?.month || ''} onChange={e => setLocalRule(p => ({ ...p, [q]: { ...p[q], month: parseInt(e.target.value) || '', day: p[q]?.day || '' } }))} />
          <span className="text-xs text-muted-foreground">Day</span>
          <Input type="number" min={1} max={31} className="h-8 w-16 text-sm" value={localRule[q]?.day || ''} onChange={e => setLocalRule(p => ({ ...p, [q]: { ...p[q], day: parseInt(e.target.value) || '', month: p[q]?.month || '' } }))} />
        </div>
      ))}
    </div>
  );

  const renderHalfYearlyForm = () => (
    <div className="space-y-3">
      {['H1','H2'].map(h => (
        <div key={h} className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-24 shrink-0">{HALF_LABELS[h]}</span>
          <span className="text-xs text-muted-foreground">Month</span>
          <Input type="number" min={1} max={12} className="h-8 w-16 text-sm" value={localRule[h]?.month || ''} onChange={e => setLocalRule(p => ({ ...p, [h]: { ...p[h], month: parseInt(e.target.value) || '', day: p[h]?.day || '' } }))} />
          <span className="text-xs text-muted-foreground">Day</span>
          <Input type="number" min={1} max={31} className="h-8 w-16 text-sm" value={localRule[h]?.day || ''} onChange={e => setLocalRule(p => ({ ...p, [h]: { ...p[h], day: parseInt(e.target.value) || '', month: p[h]?.month || '' } }))} />
        </div>
      ))}
    </div>
  );

  const renderAnnualForm = () => (
    <div className="flex items-center gap-3">
      <label className="text-xs text-muted-foreground w-24 shrink-0">Due Date</label>
      <span className="text-xs text-muted-foreground">Month</span>
      <Input type="number" min={1} max={12} className="h-8 w-16 text-sm" value={localRule.month || ''} onChange={e => setLocalRule(p => ({ ...p, month: parseInt(e.target.value) || '' }))} />
      <span className="text-xs text-muted-foreground">Day</span>
      <Input type="number" min={1} max={31} className="h-8 w-16 text-sm" value={localRule.day || ''} onChange={e => setLocalRule(p => ({ ...p, day: parseInt(e.target.value) || '' }))} />
      <span className="text-xs text-muted-foreground">(of FY+1 year)</span>
    </div>
  );

  return (
    <Card className="border-dashed" data-testid="due-date-settings">
      <button className="w-full flex items-center justify-between px-4 py-3 text-left" onClick={onToggle}>
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Due Date Settings</span>
          {hasRule && <Badge variant="secondary" className="text-[10px] bg-green-50 text-green-700 border-green-200">Configured</Badge>}
          {!hasRule && <Badge variant="secondary" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Not Set</Badge>}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <CardContent className="pt-0 pb-4 px-4 space-y-4">
          <p className="text-xs text-muted-foreground">Configure when this subtask is due. This enables it to appear in the Compliance Calendar and Radar.</p>
          {freq === 'monthly' && renderMonthlyForm()}
          {freq === 'quarterly' && renderQuarterlyForm()}
          {freq === 'half_yearly' && renderHalfYearlyForm()}
          {(freq === 'annual' || freq === 'annually' || freq === '3_yearly') && renderAnnualForm()}
          {freq === 'one_time' && <p className="text-xs text-muted-foreground">One-time subtasks do not have recurring due dates.</p>}
          <div className="flex items-center gap-2 pt-2">
            <Button size="sm" className="h-8 text-xs" onClick={handleSave} disabled={saving} data-testid="save-due-date-rule">
              {saving ? 'Saving...' : 'Save Due Date Rule'}
            </Button>
            {hasRule && (
              <Button size="sm" variant="ghost" className="h-8 text-xs text-red-500 hover:text-red-700" onClick={handleClear} disabled={saving}>
                Clear Rule
              </Button>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const SubtaskDetailPage = () => {
  const { boardId, taskId, subtaskId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [board, setBoard] = useState(null);
  const [clients, setClients] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('grid');
  const [noteInputs, setNoteInputs] = useState({});
  const [viewMode, setViewMode] = useState('complete');
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [stFilterClient, setStFilterClient] = useState('');
  const [stFilterStaff, setStFilterStaff] = useState('');
  const [stFilterStatus, setStFilterStatus] = useState('');
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [gstSyncOpen, setGstSyncOpen] = useState(false);
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [dueDateSaving, setDueDateSaving] = useState(false);

  const canManage = user?.role === 'admin' || user?.role === 'staff';
  const isAdmin = user?.role === 'admin';

  const loadData = useCallback(async () => {
    try {
      const [taskRes, boardRes, clientsRes, staffRes, directorsRes] = await Promise.all([
        tasksAPI.get(taskId),
        boardsAPI.get(boardId),
        canManage ? clientsAPI.list() : Promise.resolve({ data: [] }),
        canManage ? usersAPI.list({ role: 'staff' }) : Promise.resolve({ data: [] }),
        canManage ? directorsAPI.list().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setTask(taskRes.data);
      setBoard(boardRes.data);
      setClients(clientsRes.data);
      setStaffMembers(staffRes.data || []);
      setDirectors(directorsRes.data || []);
    } catch (err) {
      toast.error('Failed to load subtask data');
    } finally {
      setLoading(false);
    }
  }, [taskId, boardId, canManage]);

  useEffect(() => { loadData(); }, [loadData]);

  const subtask = useMemo(() => {
    if (!task) return null;
    return (task.subtasks || []).find(st => st.id === subtaskId) || null;
  }, [task, subtaskId]);

  // Set default selected period when subtask/periods change
  useEffect(() => {
    if (subtask && !selectedPeriod) {
      const freq = subtask.frequency || task?.default_frequency || 'monthly';
      const periods = getPeriodsForFrequency(freq, task?.fy_year);
      if (periods.length > 0) setSelectedPeriod(periods[0]);
    }
  }, [subtask, selectedPeriod, task]);

  // Boards that should show company_name instead of client name
  const useCompanyName = board && /GST|TDS|Tax Audit|Statutory Audit|Trust Audit/i.test(board.name);
  // True when this subtask grids by directors instead of clients (e.g. DIR-3 KYC)
  const isDirectorEntity = (subtask?.entity_type === 'director');

  const getClientName = useCallback((clientId) => {
    if (isDirectorEntity) {
      const d = directors.find(x => x.id === clientId);
      return d ? `${d.name}${d.din ? ' · ' + d.din : ''}` : '?';
    }
    const cl = clients.find(c => c.id === clientId);
    if (!cl) return '?';
    if (useCompanyName) return cl.company_name || cl.name || '?';
    return cl.name || '?';
  }, [clients, directors, isDirectorEntity, useCompanyName]);

  const getClientDisplayName = useCallback((clientId, gstin) => {
    const baseName = getClientName(clientId);
    return formatClientWithGstin(baseName, gstin);
  }, [getClientName]);

  const getClientGstinObj = useCallback((clientId, gstinNumber) => {
    if (!gstinNumber) return null;
    const cl = clients.find(c => c.id === clientId);
    if (!cl || !Array.isArray(cl.gstins)) return null;
    return cl.gstins.find(g => (typeof g === 'object' ? g.number : g) === gstinNumber) || null;
  }, [clients]);

  const getStaffName = useCallback((staffId) => {
    if (!staffId) return null;
    const s = staffMembers.find(m => m.id === staffId);
    return s?.name || s?.email || null;
  }, [staffMembers]);

  const handlePeriodStatusChange = async (clientId, period, newStatus, gstin) => {
    try {
      await tasksAPI.updateSubtaskClientPeriodStatus(taskId, subtaskId, clientId, period, { status: newStatus }, gstin);
      await loadData();
    } catch (err) { toast.error('Failed to update status'); }
  };

  const handleSubtaskStaffAssign = async (staffId, force = false) => {
    try {
      const { data } = await tasksAPI.assignSubtaskStaff(taskId, subtaskId, { assigned_to: staffId || null, force });
      toast.success(data?.message || (staffId ? 'Default team member set' : 'Default unset'));
      await loadData();
    } catch (err) { toast.error('Failed to assign team member'); }
  };

  const handleSubtaskStaffForceAssign = async (staffId) => {
    if (!staffId) return handleSubtaskStaffAssign(null, true);
    if (!window.confirm('This will overwrite EVERY client row in this subtask, including any per-client overrides. Continue?')) return;
    return handleSubtaskStaffAssign(staffId, true);
  };

  const handleClientStaffAssign = async (clientId, staffId, gstin) => {
    try {
      await tasksAPI.assignClientStaff(taskId, subtaskId, clientId, { assigned_to: staffId || null }, gstin);
      await loadData();
    } catch (err) { toast.error('Failed to assign team member'); }
  };

  const handleClientToggle = async (clientId) => {
    if (!subtask) return;
    const currentClientIds = (subtask.clients || []).map(c => c.client_id);
    const newClientIds = currentClientIds.includes(clientId)
      ? currentClientIds.filter(id => id !== clientId)
      : [...currentClientIds, clientId];
    try {
      await tasksAPI.updateSubtask(taskId, subtaskId, { client_ids: newClientIds });
      await loadData();
    } catch (err) { toast.error('Failed to update client'); }
  };

  const handleApplyClientPicker = async (selectedIds) => {
    try {
      await tasksAPI.updateSubtask(taskId, subtaskId, { client_ids: selectedIds });
      await loadData();
      toast.success('Client list updated');
    } catch (err) { toast.error('Failed to update client list'); }
  };

  const handleAddNote = async (clientId, inputKey) => {
    const key = inputKey || clientId;
    const text = (noteInputs[key] || '').trim();
    if (!text) return;
    try {
      await tasksAPI.addClientNote(taskId, subtaskId, clientId, { text });
      setNoteInputs(prev => ({ ...prev, [key]: '' }));
      await loadData();
    } catch (err) { toast.error('Failed to add note'); }
  };

  const handleOpenTaskChat = async (clientId) => {
    const clientName = getClientName(clientId) || 'Client';
    try {
      const res = await chatAPI.getOrCreateTaskThread({
        board_id: boardId,
        client_id: clientId,
        board_name: board?.name || 'Board',
        client_name: clientName,
      });
      navigate(`/dashboard/chat?group=${res.data.id}`);
    } catch (err) { toast.error('Failed to open chat thread'); }
  };

  const handleSaveDueDateRule = async (rule) => {
    setDueDateSaving(true);
    try {
      await tasksAPI.updateSubtask(taskId, subtaskId, { due_date_rule: rule });
      toast.success('Due date rule saved');
      await loadData();
    } catch (err) { toast.error('Failed to save due date rule'); }
    finally { setDueDateSaving(false); }
  };

  const subtaskData = subtask || {};
  const freq = subtaskData.frequency || task?.default_frequency || 'monthly';
  const periods = getPeriodsForFrequency(freq, task?.fy_year);
  const subtaskClients = useMemo(() => subtaskData.clients || [], [subtaskData.clients]);
  const activeStages = subtaskData.custom_stages || DEFAULT_STAGES;

  // Per-status cell counts for the filter dropdown — count every CELL
  // (client × period) currently in each status across the whole subtask.
  // Powers the "(47)" suffix beside each filter option so admins can see
  // distribution at a glance without applying the filter.
  const statusCounts = useMemo(() => {
    const counts = {};
    activeStages.forEach(s => { counts[s] = 0; });
    subtaskClients.forEach(ce => {
      const cp = ce.periods || {};
      periods.forEach(p => {
        const s = cp[p] || 'not_started';
        if (counts[s] !== undefined) counts[s] += 1;
      });
    });
    return counts;
  }, [subtaskClients, periods, activeStages]);

  const [sort, setSort] = useState({ field: null, dir: null });

  const filteredSubtaskClients = useMemo(() => {
    const q = stFilterClient.trim().toLowerCase();
    const f = subtaskClients.filter(ce => {
      if (q) {
        // Match against display name (incl. GSTIN suffix), raw client name,
        // company_name, GSTIN number and PAN — so users can search by any of them.
        const cl = clients.find(c => c.id === ce.client_id);
        const haystacks = [
          getClientDisplayName(ce.client_id, ce.gstin) || '',
          cl?.name || '',
          cl?.company_name || '',
          ce.gstin || '',
          cl?.pan || '',
        ];
        if (!haystacks.some(h => h.toLowerCase().includes(q))) return false;
      }
      if (stFilterStaff && stFilterStaff !== 'all') {
        if (ce.assigned_to !== stFilterStaff) return false;
      }
      if (stFilterStatus && stFilterStatus !== 'all') {
        const cp = ce.periods || {};
        const hasStatus = periods.some(p => (cp[p] || 'not_started') === stFilterStatus);
        if (!hasStatus) return false;
      }
      return true;
    });
    if (!sort.field || !sort.dir) {
      // Default to alphabetical (case-insensitive) by client display name when
      // the user hasn't picked a sort. Portal-wide rule.
      return [...f].sort((a, b) =>
        getClientDisplayName(a.client_id, a.gstin).toLowerCase()
          .localeCompare(getClientDisplayName(b.client_id, b.gstin).toLowerCase())
      );
    }
    const getter = (ce) => {
      if (sort.field === 'name') return getClientDisplayName(ce.client_id, ce.gstin);
      if (sort.field === 'staff') return getStaffName(ce.assigned_to);
      if (sort.field.startsWith('period:')) {
        const p = sort.field.slice(7);
        return (ce.periods || {})[p] || 'not_started';
      }
      return null;
    };
    return sortBy(f, getter, sort.dir);
  }, [subtaskClients, stFilterClient, stFilterStaff, stFilterStatus, periods, clients, getClientDisplayName, getStaffName, sort]);

  const hasSubtaskFilters = stFilterClient || (stFilterStaff && stFilterStaff !== 'all') || (stFilterStatus && stFilterStatus !== 'all');

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div>;
  if (!task || !subtask) return <div className="flex items-center justify-center h-64 text-muted-foreground">Subtask not found</div>;

  const freqBadge = FREQ_BADGE[freq] || FREQ_BADGE.monthly;

  // Stats — `filed`, `paid`, `not_to_be_filed`, `matched`, and
  // `tax_not_to_be_paid` are all "completed/closed" → not overdue, not pending.
  const totalClients = subtaskClients.length;
  let filedCount = 0, overdueCount = 0, pendingCount = 0;
  const completedStatuses = new Set((['filed', 'paid', 'not_to_be_filed', 'matched', 'tax_not_to_be_paid']));
  subtaskClients.forEach(ce => {
    const cp = ce.periods || {};
    periods.forEach(p => {
      const status = cp[p] || 'not_started';
      const dueDate = getDueDate(subtask.title, p, freq, subtaskData.due_date_rule);
      if (completedStatuses.has(status)) filedCount++;
      else if (isOverdue(dueDate)) overdueCount++;
      else pendingCount++;
    });
  });
  const totalCells = totalClients * periods.length;

  return (
    <>
    <div className="space-y-6" data-testid="subtask-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/dashboard/boards/${boardId}`)} data-testid="back-to-board">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{subtask.title}</h1>
          <p className="text-sm text-muted-foreground">{task.title}</p>
        </div>
        <Badge variant="outline" className={`text-xs px-2 py-0.5 border ${freqBadge.color}`}>{freqBadge.label}</Badge>
        {isAdmin && (
          <Select value={subtask.assigned_to || '_none'} onValueChange={(v) => handleSubtaskStaffAssign(v === '_none' ? null : v)}>
            <SelectTrigger className="h-9 w-[180px] text-sm border-dashed" data-testid="subtask-staff-assign">
              <SelectValue>{subtask.assigned_to ? getStaffName(subtask.assigned_to) || 'Team' : 'Assign Team'}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none"><span className="text-sm text-muted-foreground">Unassign</span></SelectItem>
              {staffMembers.map(s => (
                <SelectItem key={s.id} value={s.id}><span className="text-sm">{s.name || s.email}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {canManage && GST_RETURN_TITLES.has(subtask.title) && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            onClick={() => setGstSyncOpen(true)}
            data-testid="gst-sync-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Sync GST Status
          </Button>
        )}
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-amber-600 border-amber-200 hover:bg-amber-50"
            onClick={() => setReminderDialogOpen(true)}
            data-testid="set-reminder-btn"
          >
            <Bell className="h-3.5 w-3.5" /> Set Reminder
          </Button>
        )}
      </div>

      {/* Cross-board auto-sync indicator (visible on the consolidated board's
          subtask AND on the audit subtasks where it appears as a checklist row). */}
      {(subtask.title === 'Drafting of Financial Statements' || subtask.title === 'GST ITC Reconciliation') && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-sky-200 bg-sky-50/60 text-sky-900 text-xs" data-testid="consolidated-tracker-sync-indicator">
          <RefreshCw className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            <span className="font-semibold">Auto-synced</span> with the &ldquo;{subtask.title}&rdquo; row inside the Statutory Audit and Tax Audit subtasks. Status changes on either side propagate in real-time.
          </span>
        </div>
      )}

      {/* Due Date Settings — Admin only */}
      {isAdmin && (
        <DueDateSettings
          freq={freq}
          rule={subtaskData.due_date_rule}
          open={dueDateOpen}
          onToggle={() => setDueDateOpen(p => !p)}
          onSave={handleSaveDueDateRule}
          saving={dueDateSaving}
        />
      )}

      {/* Subtask-level shared note — Audit boards only (Statutory / Tax / Trust) */}
      {board && /audit/i.test(board.name || '') && (
        <SubtaskNotePanel
          task={task}
          subtask={subtask}
          canManage={canManage}
          onReload={loadData}
        />
      )}

      {/* Checklist Mode — for subtasks with checklist_items */}
      {(() => {
        const isOneTimeBoard = board?.name === 'GST One-Time Activities';
        const oneTimeKinds = { 'gst registration': 'registration', 'gst cancellation': 'cancellation', 'gst amendment': 'amendment', 'gst notice': 'notice' };
        const isOneTime = isOneTimeBoard && oneTimeKinds[(subtask.title || '').toLowerCase()];
        if (isOneTime) {
          return (
            <OneTimeActivityGrid
              task={task}
              subtask={subtask}
              clients={clients}
              staffMembers={staffMembers}
              isAdmin={isAdmin}
              canManage={canManage}
              onReload={loadData}
              STAGE_LABELS={STAGE_LABELS}
              STATUS_SHORT={STATUS_SHORT}
              STATUS_CELL_COLORS={STATUS_CELL_COLORS}
            />
          );
        }
        return null;
      })()}
      {!(board?.name === 'GST One-Time Activities' && ['gst registration', 'gst cancellation', 'gst amendment', 'gst notice'].includes((subtask.title || '').toLowerCase())) && (
      <>
      {(subtask.checklist_items || []).length > 0 ? (
        <ChecklistGrid
          task={task}
          subtask={subtask}
          clients={clients}
          staffMembers={staffMembers}
          periods={periods}
          getPeriodLabel={getPeriodLabel}
          canManage={canManage}
          isAdmin={isAdmin}
          onReload={loadData}
        />
      ) : (
      <>
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><Users2 className="h-5 w-5 text-primary" /></div>
            <div><div className="text-2xl font-bold">{totalClients}</div><div className="text-xs text-muted-foreground">{isDirectorEntity ? 'Directors' : 'Clients'}</div></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-emerald-600" /></div>
            <div><div className="text-2xl font-bold">{filedCount}</div><div className="text-xs text-muted-foreground">{activeStages.includes('paid') ? 'Paid' : 'Filed'} ({totalCells > 0 ? Math.round(filedCount / totalCells * 100) : 0}%)</div></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="h-5 w-5 text-amber-600" /></div>
            <div><div className="text-2xl font-bold">{pendingCount}</div><div className="text-xs text-muted-foreground">Pending</div></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-red-500" /></div>
            <div><div className="text-2xl font-bold">{overdueCount}</div><div className="text-xs text-muted-foreground">Overdue</div></div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b">
        <button
          onClick={() => { setActiveTab('grid'); setViewMode('complete'); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'grid' && viewMode === 'complete' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-complete"
        >Complete Mode</button>
        <button
          onClick={() => { setActiveTab('grid'); setViewMode('monthly'); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'grid' && viewMode === 'monthly' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-monthly"
        >Monthly Mode</button>
        <button
          onClick={() => setActiveTab('notes')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'notes' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-notes"
        ><MessageSquare className="h-4 w-4" /> Notes</button>
      </div>

      {/* Subtask Client Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground mr-1"><Filter className="h-3.5 w-3.5 inline mr-1" />Filters:</span>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Client name..." value={stFilterClient} onChange={e => setStFilterClient(e.target.value)} className="h-8 text-xs w-40 pl-7" data-testid="subtask-filter-client" />
        </div>
        <Select value={stFilterStaff || 'all'} onValueChange={v => setStFilterStaff(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 text-xs w-36" data-testid="subtask-filter-staff-trigger">
            <SelectValue placeholder="All Team" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Team</SelectItem>
            {staffMembers.map(s => <SelectItem key={s.id} value={s.id}><span className="text-xs">{s.name || s.email}</span></SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stFilterStatus || 'all'} onValueChange={v => setStFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 text-xs w-40" data-testid="subtask-filter-status-trigger">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses ({totalCells})</SelectItem>
            {/* Restrict the dropdown to statuses ACTUALLY allowed on this
                subtask (custom_stages). Eg. GSTR-1 must not show "Tax Paid"
                / "Mismatched" / "Tax Not to be Paid" because those stages
                belong to TDS Payment / ITC Reconciliation flows.
                The trailing (count) shows current cell distribution. */}
            {activeStages.filter(key => STAGE_LABELS[key]).map(key => (
              <SelectItem key={key} value={key}>
                <span className="text-xs">
                  {STATUS_SHORT[key]} — {STAGE_LABELS[key]}
                  <span className="text-muted-foreground ml-1">({statusCounts[key] || 0})</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasSubtaskFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 text-muted-foreground hover:text-red-500" onClick={() => { setStFilterClient(''); setStFilterStaff(''); setStFilterStatus(''); }} data-testid="clear-subtask-filters">
            <X className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
        {hasSubtaskFilters && (
          <span className="text-xs text-muted-foreground ml-auto">{filteredSubtaskClients.length} of {subtaskClients.length} clients</span>
        )}
      </div>

      {/* Complete Mode */}
      {activeTab === 'grid' && viewMode === 'complete' && (
        <>
      {/* Status Legend */}
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-muted-foreground font-medium mr-1">Status:</span>
        {activeStages.map(key => (
          <span key={key} className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium ${STATUS_CELL_COLORS[key] || 'bg-gray-50 text-gray-500'}`}>
            {STATUS_SHORT[key] || key} = {STAGE_LABELS[key] || key}
          </span>
        ))}
      </div>

      {/* Client Grid */}
      <Card>
        <CardContent className="p-0">
          {filteredSubtaskClients.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">{hasSubtaskFilters ? 'No clients match the filters' : 'No clients assigned to this subtask'}</div>
          ) : (
            <div className="w-full overflow-x-auto">
              <div style={{ width: '100%', minWidth: FREQ_MIN_WIDTHS[freq] || FREQ_MIN_WIDTHS.monthly }}>
                {/* Header */}
                <div className="flex items-center border-b bg-muted/30 sticky top-0 z-10">
                  <div className="flex-1 min-w-[256px] px-5 py-3 sticky left-0 bg-muted/30 z-20">
                    <SortableHeader field="name" sort={sort} onChange={setSort}>{isDirectorEntity ? 'Director' : 'Client'}</SortableHeader>
                  </div>
                  {canManage && <div className="w-[140px] flex-shrink-0 px-2 py-3 border-l sticky bg-muted/30 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]" style={{ left: '256px' }}>
                    <SortableHeader field="staff" sort={sort} onChange={setSort}>Assigned Team</SortableHeader>
                  </div>}
                  {!canManage && <div className="w-0 sticky left-[256px] z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]" />}
                  {periods.map(period => {
                    const dueDate = getDueDate(subtask.title, period, freq, subtaskData.due_date_rule);
                    const periodOverdue = isOverdue(dueDate);
                    const periodDueSoon = isDueSoon(dueDate);
                    const sortField = `period:${period}`;
                    const isActive = sort.field === sortField;
                    return (
                      <div key={period} className={`flex-shrink-0 text-center py-2 border-l ${FREQ_COL_WIDTHS[freq] || FREQ_COL_WIDTHS.monthly}`}>
                        <SortableHeader field={sortField} sort={sort} onChange={setSort} align="center">
                          <span className={`text-[11px] font-semibold ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{getPeriodLabel(period)}</span>
                        </SortableHeader>
                        {dueDate && (
                          <div className={`text-[9px] font-medium mt-0.5 ${dueDateClass(periodOverdue, periodDueSoon, { overdue: 'text-red-500 font-bold', dueSoon: 'text-amber-500', normal: 'text-muted-foreground/60' })}`}>
                            Due: {formatDueDate(dueDate)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {canManage && <div className="w-10 flex-shrink-0" />}
                </div>

                {/* Client Rows */}
                {filteredSubtaskClients.map(clientEntry => {
                  const clientPeriods = clientEntry.periods || {};
                  const ceKey = clientEntry.gstin ? `${clientEntry.client_id}|${clientEntry.gstin}` : clientEntry.client_id;
                  const ceGstin = clientEntry.gstin || null;
                  const displayName = getClientDisplayName(clientEntry.client_id, ceGstin);
                  return (
                    <div key={ceKey} className="flex items-center border-b last:border-b-0 hover:bg-muted/10 transition-colors">
                      <div className="flex-1 min-w-[256px] px-5 py-2.5 flex items-center gap-2 sticky left-0 bg-background z-10">
                        <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary flex-shrink-0">
                          {displayName?.[0]?.toUpperCase()}
                        </div>
                        <span className="text-sm font-medium flex-1 min-w-0" title={displayName}>{displayName}</span>
                        <button onClick={() => handleOpenTaskChat(clientEntry.client_id)} className="text-muted-foreground hover:text-primary transition-colors p-0.5 flex-shrink-0" title="Open chat thread" data-testid={`chat-client-${ceKey}`}>
                          <MessageSquare className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {canManage && (
                        <div className="w-[140px] flex-shrink-0 px-1 py-1.5 border-l sticky bg-background z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]" style={{ left: '256px' }}>
                          {isAdmin ? (
                            <Select value={clientEntry.assigned_to || '_none'} onValueChange={(v) => handleClientStaffAssign(clientEntry.client_id, v === '_none' ? null : v, ceGstin)}>
                              <SelectTrigger className="h-8 w-full text-[11px] border-dashed px-2" data-testid={`client-staff-${ceKey}`}>
                                <SelectValue>{clientEntry.assigned_to ? (getStaffName(clientEntry.assigned_to) || 'Team') : '—'}</SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none"><span className="text-xs text-muted-foreground">Unassign</span></SelectItem>
                                {staffMembers.map(s => (
                                  <SelectItem key={s.id} value={s.id}><span className="text-xs">{s.name || s.email}</span></SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-[11px] text-muted-foreground px-2">{clientEntry.assigned_to ? (getStaffName(clientEntry.assigned_to) || 'Team') : '—'}</span>
                          )}
                        </div>
                      )}
                      {periods.map(period => {
                        const status = clientPeriods[period] || 'not_started';
                        const dueDate = getDueDate(subtask.title, period, freq, subtaskData.due_date_rule);
                        const cellOverdue = isOverdue(dueDate) && status !== 'filed' && status !== 'not_to_be_filed' && status !== 'matched' && status !== 'tax_not_to_be_paid';
                        const cellDueSoon = isDueSoon(dueDate) && status !== 'filed' && status !== 'not_to_be_filed' && status !== 'matched' && status !== 'tax_not_to_be_paid';
                        const gstinObj = getClientGstinObj(clientEntry.client_id, ceGstin);
                        const isInactive = ceGstin && gstinObj && !isPeriodActiveForGstin(period, freq, gstinObj);
                        const inactiveTip = isInactive ? inactiveReason(period, freq, gstinObj) : '';
                        const portal = (clientEntry.portal_periods || {})[period];
                        let portalDot = null;
                        if (portal && !isInactive) {
                          const teamFiled = status === 'filed';
                          if (portal.status === 'filed' && teamFiled) {
                            portalDot = { cls: 'bg-emerald-500', tip: `Portal verified: Filed${portal.filing_date ? ' on ' + portal.filing_date : ''}` };
                          } else if (portal.status === 'filed' && !teamFiled) {
                            portalDot = { cls: 'bg-amber-500', tip: `Portal shows FILED${portal.filing_date ? ' on ' + portal.filing_date : ''} but team status is not Filed` };
                          } else if (portal.status === 'not_filed' && teamFiled) {
                            portalDot = { cls: 'bg-red-500', tip: 'Team marked Filed but portal shows NOT filed' };
                          }
                        }
                        return (
                          <div key={period} className={`relative flex-shrink-0 border-l px-1 py-1.5 ${FREQ_COL_WIDTHS[freq] || FREQ_COL_WIDTHS.monthly} ${isInactive ? 'bg-muted/30' : dueDateClass(cellOverdue, cellDueSoon, { overdue: 'bg-red-50', dueSoon: 'bg-amber-50/30', normal: '' })}`}>
                            {portalDot && (
                              <span
                                className={`absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full ${portalDot.cls} z-10`}
                                title={portalDot.tip}
                                data-testid={`portal-dot-${period}-${ceKey}`}
                              />
                            )}                            {isInactive ? (
                              <div
                                className="h-8 flex items-center justify-center text-[11px] text-muted-foreground/40 rounded cursor-not-allowed select-none"
                                title={inactiveTip}
                                data-testid={`period-inactive-${period}-${ceKey}`}
                              >
                                —
                              </div>
                            ) : canManage ? (
                              <Select value={status} onValueChange={(v) => handlePeriodStatusChange(clientEntry.client_id, period, v, ceGstin)}>
                                <SelectTrigger className={`h-8 w-full text-[11px] font-bold px-1 justify-center ${dueDateClass(cellOverdue, cellDueSoon, { overdue: 'border border-red-300 ring-1 ring-red-200', dueSoon: 'border border-amber-300', normal: 'border-0' })} ${STATUS_CELL_COLORS[status]}`} data-testid={`period-status-${period}-${ceKey}`}>
                                  <SelectValue>{STATUS_SHORT[status]}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {activeStages.map(key => (
                                    <SelectItem key={key} value={key}><span className="text-xs">{STATUS_SHORT[key] || key} — {STAGE_LABELS[key] || key}</span></SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className={`h-8 flex items-center justify-center text-[11px] font-bold rounded ${cellOverdue ? 'ring-1 ring-red-300' : ''} ${STATUS_CELL_COLORS[status] || 'bg-gray-50 text-gray-500'}`}>{STATUS_SHORT[status] || status}</div>
                            )}
                          </div>
                        );
                      })}
                      {isAdmin && (
                        <div className="w-10 flex-shrink-0 flex items-center justify-center">
                          <button onClick={() => handleClientToggle(clientEntry.client_id)} className="text-muted-foreground hover:text-red-500 transition-colors p-1">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add Client */}
          {canManage && (
            <div className="px-5 py-3 border-t bg-muted/5 flex items-center gap-3">
              <button
                onClick={() => { setClientPickerOpen(true); }}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-muted border border-dashed"
                data-testid="add-client-to-subtask"
              >
                <Users2 className="h-4 w-4" /> Manage clients
              </button>
              <span className="text-xs text-muted-foreground">{subtaskClients.length} linked</span>
            </div>
          )}
        </CardContent>
      </Card>
        </>
      )}

      {/* Monthly Mode */}
      {activeTab === 'grid' && viewMode === 'monthly' && (
        <>
          {/* Period Selector */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">Period:</span>
            <Select value={selectedPeriod || ''} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-[200px] h-9" data-testid="period-selector">
                <SelectValue>{selectedPeriod ? getPeriodLabel(selectedPeriod) : 'Select'}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {periods.map(p => (
                  <SelectItem key={p} value={p}><span className="text-sm">{getPeriodLabel(p)}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedPeriod && (() => {
              const dueDate = getDueDate(subtask.title, selectedPeriod, freq, subtaskData.due_date_rule);
              const periodOverdue = isOverdue(dueDate);
              const periodDueSoon = isDueSoon(dueDate);
              return dueDate ? (
                <span className={`text-xs font-medium ${dueDateClass(periodOverdue, periodDueSoon, { overdue: 'text-red-500', dueSoon: 'text-amber-500', normal: 'text-muted-foreground' })}`}>
                  Due: {formatDueDate(dueDate)}
                </span>
              ) : null;
            })()}
          </div>

          {/* Monthly Table */}
          <Card>
            <CardContent className="p-0">
              {filteredSubtaskClients.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">{hasSubtaskFilters ? 'No clients match the filters' : 'No clients assigned'}</div>
              ) : (
                <div>
                  {/* Header */}
                  <div className="grid grid-cols-[1fr_150px_150px_1fr] border-b bg-muted/30">
                    <div className="px-5 py-3 text-xs font-semibold text-muted-foreground">Client</div>
                    {canManage && <div className="px-3 py-3 text-xs font-semibold text-muted-foreground border-l">Team</div>}
                    <div className="px-3 py-3 text-xs font-semibold text-muted-foreground border-l">Status</div>
                    <div className="px-3 py-3 text-xs font-semibold text-muted-foreground border-l">Notes</div>
                  </div>

                  {/* Rows */}
                  {filteredSubtaskClients.map(clientEntry => {
                    const status = (clientEntry.periods || {})[selectedPeriod] || 'not_started';
                    const dueDate = getDueDate(subtask.title, selectedPeriod, freq, subtaskData.due_date_rule);
                    const cellOverdue = isOverdue(dueDate) && status !== 'filed' && status !== 'not_to_be_filed' && status !== 'matched' && status !== 'tax_not_to_be_paid';
                    const cellDueSoon = isDueSoon(dueDate) && status !== 'filed' && status !== 'not_to_be_filed' && status !== 'matched' && status !== 'tax_not_to_be_paid';
                    const notes = clientEntry.notes || [];
                    const clientName = getClientDisplayName(clientEntry.client_id, ceGstin);
                    return (
                      <div key={ceKey} className={`grid grid-cols-[1fr_150px_150px_1fr] border-b last:border-b-0 ${dueDateClass(cellOverdue, cellDueSoon, { overdue: 'bg-red-50/40', dueSoon: 'bg-amber-50/20', normal: 'hover:bg-muted/10' })} transition-colors`} data-testid={`monthly-row-${ceKey}`}>
                        <div className="px-5 py-3 flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary flex-shrink-0">
                            {clientName?.[0]?.toUpperCase()}
                          </div>
                          <span className="text-sm font-medium truncate flex-1" title={clientName}>{clientName}</span>
                          <button onClick={() => handleOpenTaskChat(clientEntry.client_id)} className="text-muted-foreground hover:text-primary transition-colors p-0.5 flex-shrink-0" title="Open chat thread" data-testid={`chat-client-monthly-${ceKey}`}>
                            <MessageSquare className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {canManage && (
                          <div className="px-2 py-2 border-l flex items-center">
                            {isAdmin ? (
                              <Select value={clientEntry.assigned_to || '_none'} onValueChange={(v) => handleClientStaffAssign(clientEntry.client_id, v === '_none' ? null : v, ceGstin)}>
                                <SelectTrigger className="h-8 w-full text-[11px] border-dashed px-2">
                                  <SelectValue>{clientEntry.assigned_to ? (getStaffName(clientEntry.assigned_to) || 'Team') : '—'}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none"><span className="text-xs text-muted-foreground">Unassign</span></SelectItem>
                                  {staffMembers.map(s => (
                                    <SelectItem key={s.id} value={s.id}><span className="text-xs">{s.name || s.email}</span></SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-[11px] text-muted-foreground px-2">{clientEntry.assigned_to ? (getStaffName(clientEntry.assigned_to) || 'Team') : '—'}</span>
                            )}
                          </div>
                        )}
                        <div className="px-2 py-2 border-l flex items-center">
                          {canManage ? (
                            <Select value={status} onValueChange={(v) => handlePeriodStatusChange(clientEntry.client_id, selectedPeriod, v, ceGstin)}>
                              <SelectTrigger className={`h-8 w-full text-[11px] font-bold px-2 justify-center ${cellOverdue ? 'border border-red-300 ring-1 ring-red-200' : 'border-0'} ${STATUS_CELL_COLORS[status]}`}>
                                <SelectValue>{STAGE_LABELS[status]}</SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {activeStages.map(key => (
                                  <SelectItem key={key} value={key}><span className="text-xs">{STAGE_LABELS[key] || key}</span></SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className={`h-8 flex items-center justify-center text-[11px] font-bold rounded w-full ${STATUS_CELL_COLORS[status] || 'bg-gray-50 text-gray-500'}`}>{STAGE_LABELS[status] || status}</div>
                          )}
                        </div>
                        <div className="px-4 py-2 border-l">
                          {notes.length > 0 ? (
                            <div className="space-y-1.5 max-h-24 overflow-y-auto">
                              {notes.slice(-3).map(note => (
                                <div key={note.id} className="text-xs">
                                  <span className="font-semibold">{note.author_name}:</span>{' '}
                                  <span className="text-muted-foreground">{note.text}</span>
                                </div>
                              ))}
                              {notes.length > 3 && <span className="text-[10px] text-muted-foreground">+{notes.length - 3} more</span>}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">No notes</span>
                          )}
                          {canManage && (
                            <div className="flex gap-1.5 mt-2">
                              <input
                                type="text"
                                value={noteInputs[`m_${clientEntry.client_id}`] || ''}
                                onChange={(e) => setNoteInputs(prev => ({ ...prev, [`m_${clientEntry.client_id}`]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') { const text = (noteInputs[`m_${clientEntry.client_id}`] || '').trim(); if (text) { handleAddNote(clientEntry.client_id, `m_${clientEntry.client_id}`); } } }}
                                placeholder="Add note..."
                                className="flex-1 text-xs px-2 py-1.5 border rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary/20"
                                data-testid={`monthly-note-input-${clientEntry.client_id}`}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => handleAddNote(clientEntry.client_id, `m_${clientEntry.client_id}`)}
                                disabled={!(noteInputs[`m_${clientEntry.client_id}`] || '').trim()}
                              >
                                <Send className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add Client */}
              {canManage && (
                <div className="px-5 py-3 border-t bg-muted/5 flex items-center gap-3">
                  <button
                    onClick={() => setClientPickerOpen(true)}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors px-3 py-1.5 rounded-lg hover:bg-muted border border-dashed"
                  >
                    <Users2 className="h-4 w-4" /> Manage clients
                  </button>
                  <span className="text-xs text-muted-foreground">{subtaskClients.length} linked</span>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div className="space-y-4" data-testid="notes-tab">
          {subtaskClients.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">No clients assigned to this subtask</div>
          ) : (
            subtaskClients.map(clientEntry => {
              const notes = clientEntry.notes || [];
              const clientName = getClientName(clientEntry.client_id);
              return (
                <Card key={clientEntry.client_id}>
                  <CardContent className="p-0">
                    <div className="px-5 py-3 border-b bg-muted/20 flex items-center gap-2">
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary flex-shrink-0">
                        {clientName?.[0]?.toUpperCase()}
                      </div>
                      <span className="font-medium text-sm">{clientName}</span>
                      <Badge variant="secondary" className="text-[10px] ml-auto">{notes.length} note{notes.length !== 1 ? 's' : ''}</Badge>
                    </div>
                    <div className="px-5 py-3 space-y-3">
                      {notes.length === 0 && (
                        <p className="text-sm text-muted-foreground italic">No notes yet</p>
                      )}
                      {notes.map(note => (
                        <div key={note.id} className="flex gap-3" data-testid={`note-${note.id}`}>
                          <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground flex-shrink-0 mt-0.5">
                            {(note.author_name || '?')[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="text-xs font-semibold">{note.author_name}</span>
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(note.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}, {new Date(note.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p className="text-sm mt-0.5 whitespace-pre-wrap">{note.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {canManage && (
                      <div className="px-5 py-3 border-t flex gap-2">
                        <input
                          type="text"
                          value={noteInputs[clientEntry.client_id] || ''}
                          onChange={(e) => setNoteInputs(prev => ({ ...prev, [clientEntry.client_id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleAddNote(clientEntry.client_id); }}
                          placeholder="Type a note..."
                          className="flex-1 text-sm px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                          data-testid={`note-input-${clientEntry.client_id}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleAddNote(clientEntry.client_id)}
                          disabled={!(noteInputs[clientEntry.client_id] || '').trim()}
                          data-testid={`note-submit-${clientEntry.client_id}`}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
      </>
      )}
      </>
      )}
    </div>

      {reminderDialogOpen && (
        <ReminderDialog
          open={reminderDialogOpen}
          onOpenChange={setReminderDialogOpen}
          taskId={taskId}
          subtaskId={subtaskId}
          subtaskTitle={subtask?.title || 'Subtask'}
          frequency={subtask?.frequency}
          dueDateRule={subtask?.due_date_rule}
        />
      )}

      {gstSyncOpen && (
        <GstSyncDialog
          open={gstSyncOpen}
          onOpenChange={setGstSyncOpen}
          taskId={taskId}
          subtaskId={subtaskId}
          subtaskTitle={subtask?.title || 'Subtask'}
          onChanged={loadData}
        />
      )}

      <ClientPickerDialog
        open={clientPickerOpen}
        onOpenChange={setClientPickerOpen}
        clients={clients}
        linkedIds={subtaskClients.map(c => c.client_id)}
        onApply={handleApplyClientPicker}
        title={`Manage clients for ${subtask?.title || 'subtask'}`}
        subtitle={`${subtaskClients.length} clients currently linked. Tick to add, untick to remove.`}
      />
    </>
  );
};

export default SubtaskDetailPage;
